import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { categoriesService } from "../../../services/categoriesService";
import { skillsService } from "../../../services/skillsService";
import { AdminLayout } from "../../layout/AdminLayout";
import { toast } from "react-toastify";

export const CategoryForm = () => {
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [allSkills, setAllSkills] = useState([]);
	const [selectedSkills, setSelectedSkills] = useState([]);
	const [loadingSkills, setLoadingSkills] = useState(true);
	const [loading, setLoading] = useState(false);
	const [isNew, setIsNew] = useState(true);
	const { id } = useParams();
	const navigate = useNavigate();

	useEffect(() => {
		loadSkills();
	}, []);

	useEffect(() => {
		if (id && id !== "new") {
			setIsNew(false);
			loadCategory(id);
		}
	}, [id]);

	const loadSkills = async () => {
		try {
			setLoadingSkills(true);
			const data = await skillsService.getAll(1, 1000, "", "name", "asc");
			setAllSkills(data.data || []);
		} catch (error) {
			console.error("Error loading skills:", error);
			toast.error("Failed to load skills");
		} finally {
			setLoadingSkills(false);
		}
	};

	const loadCategory = async (catId) => {
		try {
			const data = await categoriesService.getById(catId);
			setName(data.name);
			setDescription(data.description || "");
			setSelectedSkills(
				(data.skills || []).map((skill) =>
					typeof skill === "string" ? skill : skill._id,
				),
			);
		} catch (error) {
			console.error("Error loading category:", error);
			toast.error("Failed to load category");
			navigate("/admin/categories");
		}
	};

	const toggleSkill = (skillId) => {
		if (selectedSkills.includes(skillId)) {
			setSelectedSkills(selectedSkills.filter((id) => id !== skillId));
		} else {
			setSelectedSkills([...selectedSkills, skillId]);
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setLoading(true);

		try {
			const formData = { name, description, skills: selectedSkills };

			if (isNew) {
				await categoriesService.create(formData);
				toast.success("Category created");
			} else {
				await categoriesService.update(id, formData);
				toast.success("Category updated");
			}

			navigate("/admin/categories");
		} catch (error) {
			console.error("Error saving category:", error);
			toast.error("Failed to save category");
		} finally {
			setLoading(false);
		}
	};

	return (
		<AdminLayout>
			<div className="p-6">
				<div className="max-w-2xl mx-auto">
					<h1 className="text-3xl font-bold text-gray-800 mb-6">
						{isNew ? "Add Category" : "Edit Category"}
					</h1>

					<div className="bg-white rounded-lg shadow p-6">
						<form onSubmit={handleSubmit} className="space-y-6">
							<div>
								<label
									htmlFor="category-name"
									className="block text-sm font-medium text-gray-700 mb-2"
								>
									Category Name *
								</label>
								<input
									id="category-name"
									type="text"
									value={name}
									onChange={(e) => setName(e.target.value)}
									required
									maxLength={255}
									className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
									placeholder="e.g., Backend Development"
								/>
							</div>

							<div>
								<label
									htmlFor="category-description"
									className="block text-sm font-medium text-gray-700 mb-2"
								>
									Description
								</label>
								<textarea
									id="category-description"
									value={description}
									onChange={(e) => setDescription(e.target.value)}
									className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
									rows="4"
									placeholder="Category description"
								/>
							</div>

							<div>
								<label
									htmlFor="category-skills-list"
									className="block text-sm font-medium text-gray-700 mb-2"
								>
									Skills
								</label>

								{loadingSkills ? (
									<div className="text-sm text-gray-500">Loading skills...</div>
								) : allSkills.length === 0 ? (
									<div className="text-sm text-gray-500">
										No skills available. Add skills first.
									</div>
								) : (
									<div
										id="category-skills-list"
										className="max-h-72 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100"
									>
										{allSkills.map((skill) => (
											<label
												key={skill._id}
												className="flex items-start gap-3 p-3 hover:bg-gray-50"
											>
												<input
													type="checkbox"
													checked={selectedSkills.includes(skill._id)}
													onChange={() => toggleSkill(skill._id)}
													className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
												/>
												<div>
													<p className="font-medium text-gray-800">
														{skill.name}
													</p>
													{skill.description && (
														<p className="text-xs text-gray-600">
															{skill.description}
														</p>
													)}
												</div>
											</label>
										))}
									</div>
								)}

								<p className="mt-2 text-xs text-gray-500">
									Selected: {selectedSkills.length} skill
									{selectedSkills.length === 1 ? "" : "s"}
								</p>
							</div>

							<div className="flex gap-2">
								<button
									type="submit"
									disabled={loading}
									className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-2 rounded-lg transition"
								>
									{loading ? "Saving..." : "Save Category"}
								</button>
								<button
									type="button"
									onClick={() => navigate("/admin/categories")}
									className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium py-2 rounded-lg transition"
								>
									Cancel
								</button>
							</div>
						</form>
					</div>
				</div>
			</div>
		</AdminLayout>
	);
};
